/**
 * <strong>Purpose:</strong> <br><br>
 * Provides mutual exclusion among widget instances which have the checked state. <br>
 * When the widget is instantiated:
 *  a. With a button-group, the mixin ensures that only one of the instances in the specified button-group has its
 *     checked property set to true.
 *  b. Without a button-group, this mixin does not have any effect.
 * <br><br>
 * <strong>Mixin Dependencies:</strong> <br>
 * <br>
 * <li>{@link module:mw-form/mixins/property/CheckedMixin|CheckedMixin}</li>
 * <br>
 * <li>{@link module:mw-form/mixins/event/ChangeEventMixin|ChangeEventMixin}</li>
 *
 * @module mw-form/mixins/property/ButtonGroupMixin
 *
 * @copyright Copyright 2014 The MathWorks, Inc.
 */

define([
    "dojo/_base/declare",
    "dojo/query",
    "dijit/registry",
    "mw-form/mixins/property/CheckedMixin",
    "mw-form/mixins/event/ChangeEventMixin"
], function (declare, query, registry, CheckedMixin, ChangeEventMixin) {

    var ButtonGroupMixin = declare(null, {

        /**
         * @property  {string} buttonGroup
         * @instance
         */
        buttonGroup: "",

        postCreate: function () {
            this.inherited(arguments);
            this.domNode.classList.add("mwButtonGroupMixin");
        },

        // Maintain a data tag to on the domNode to identify this type of widget
        // This is used in the checked setter
        _setButtonGroupAttr: function (buttonGroup) {
            if (typeof buttonGroup !== "string") {
                throw new Error("'buttonGroup' property expects a literal string!");
            }

            if (buttonGroup.length > 0) {
                this.domNode.setAttribute("data-mw-buttongroup", buttonGroup);
            } else {
                this.domNode.removeAttribute("data-mw-buttongroup");
            }

            this._set("buttonGroup", buttonGroup);
        },

        // Override from CheckedMixin
        // The second parameter "isUserEvent" is used to determine if the change event needs to be triggered
        // And this happens in the _onClick method of the CheckedMixin
        _setCheckedAttr: function (checked, isUserEvent) {

            this.inherited(arguments);

            // If the new value of this instance is true AND if it is part of a button-group
            // Then find the other checked button in the group and uncheck it
            if (checked && this.get("buttonGroup")) {
                registry.toArray().forEach(function (widget) {
                    if (this._shouldUncheckWidget(widget)) {
                        // the other radio-button in this group that is currently checked
                        widget.set("checked", false, isUserEvent);
                        // emit an event only if this setter was triggered from the _onClick of CheckedMixin
                        if (isUserEvent) {
                            widget._triggerChangeEvent("checked", true, false);
                        }
                    }
                }, this);
            }
        },

        _shouldUncheckWidget: function(widget) {
            return (widget.get("buttonGroup") === this.get("buttonGroup") && widget !== this && widget.get("checked"));
        },


        // Override from _CheckedMixin
        _onClick: function (event) {
            // No-op when the user clicks on a checked button which is part of a group except if deselection is allowed
            if (this.get("checked") && this.get("buttonGroup") && !this.get("allowDeselectionInButtonGroup")) {
                event.stopPropagation();
                event.preventDefault();
                return false;
            }
            // any other button in the group: defer to the default behavior in CheckedMixin
            this.inherited(arguments);
        }
    });

    ButtonGroupMixin._dependencies = [
        {mixin: CheckedMixin, orderDependent: true},
        {mixin: ChangeEventMixin, orderDependent: false}
    ];
    return ButtonGroupMixin;

});
